import React, { useState, useEffect, useRef } from 'react';
import { X, MessageCircle } from 'lucide-react';

export interface NotificationData {
  id: string;
  title: string;
  message: string;
  image?: string;
  type?: 'message' | 'alert';
}

interface InAppNotificationProps {
  notification: NotificationData | null;
  onDismiss: () => void;
  onOpen: () => void;
}

export const InAppNotification: React.FC<InAppNotificationProps> = ({ 
  notification, 
  onDismiss,
  onOpen 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const startY = useRef<number>(0);
  const currentY = useRef<number>(0);
  const notificationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (notification) {
      setIsVisible(true);
      // Auto-dismiss after 5 seconds
      const timer = setTimeout(() => {
        handleDismiss();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const handleDismiss = () => {
    setIsVisible(false);
    // Wait for animation to finish before clearing data
    setTimeout(onDismiss, 300);
  };

  // --- Swipe Logic ---
  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    startY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    currentY.current = e.touches[0].clientY;
    const diff = currentY.current - startY.current;
    
    // Only allow dragging UP (negative diff)
    if (diff < 0 && notificationRef.current) {
      notificationRef.current.style.transform = `translateY(${diff}px)`;
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    const diff = currentY.current - startY.current;
    
    // If swiped up more than 50px, dismiss
    if (diff < -50) {
      handleDismiss();
    } else {
      // Reset position if swipe wasn't strong enough
      if (notificationRef.current) {
        notificationRef.current.style.transform = 'translateY(0)';
      }
    }
  };

  if (!notification && !isVisible) return null;

  return (
    <div 
      className={`fixed top-0 left-0 right-0 z-[100] p-4 transition-all duration-300 ease-out pointer-events-none ${
        isVisible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
      }`}
    >
      <div 
        ref={notificationRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={onOpen}
        className="mx-auto max-w-sm w-full bg-zinc-900/95 backdrop-blur-md border border-white/10 rounded-2xl shadow-2xl shadow-black/50 p-4 flex items-center gap-4 pointer-events-auto cursor-pointer active:scale-95 transition-transform"
      >
        {/* Avatar / Icon */}
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center overflow-hidden">
          {notification?.image ? (
            <img src={notification.image} alt="" className="w-full h-full object-cover" />
          ) : (
            <MessageCircle size={20} className="text-white" />
          )}
        </div>

        {/* Text Content */}
        <div className="flex-1 min-w-0">
          <h4 className="text-white text-sm font-bold truncate">{notification?.title}</h4>
          <p className="text-zinc-400 text-xs truncate">{notification?.message}</p>
        </div>

        {/* Time / Dismiss Indicator */}
        <div className="flex flex-col items-end gap-1">
           <span className="text-[10px] text-zinc-600 font-bold">NOW</span>
           <div className="w-8 h-1 bg-zinc-700 rounded-full mt-1" /> {/* Swipe Handle */}
        </div>
      </div>
    </div>
  );
};